import React from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import {
  useAddUser,
  useEditUserForm,
  useGetUserForm,
  useUpdateUser,
} from '../../hooks/react-query/useAdmin';
import { useGetRoles } from '../../hooks/react-query/useUserRoles';
import ReactPhoneInput from 'react-phone-input-2';
import el from '../../locale/react-phone-input-2/el.json';
import { TextField, Checkbox } from '../ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/Dropdown';
import { useUserTypes } from '../../hooks/react-query/useOffer';
import { ModalFooter } from '../elements/modalFooter';
import { FormSkeletion } from '../elements/skeletions';
import { NoDataFound } from '../elements/noDataFound';
import { toast } from '../ui/Toast/useToast';

export const UserForm = ({ userId, handleClose, mode = 'add' }) => {
  const { data: userTypes, isLoading: isUserTypesLoading } = useUserTypes();
  const { data: addFormData, isLoading: isUserFormLoading } =
    useGetUserForm(userId);
  const { data: editFormData, isLoading: isEditFormLoading } =
    useEditUserForm(userId);
  const { data: userRoles, isLoading } = useGetRoles();

  const { mutate: addUser, isLoading: isAddLoading } = useAddUser();
  const { mutate: updateUser, isLoading: isEditLoading } = useUpdateUser();
  const userFormData = userId ? editFormData : addFormData;

  if (
    (!userId && isUserFormLoading) ||
    (userId && isEditFormLoading) ||
    isLoading ||
    isUserTypesLoading
  ) {
    return (
      <div className={`w-full flex justify-center h-full`}>
        <FormSkeletion />
      </div>
    );
    // create a common loading component
  }

  if (!userFormData || !userTypes || !userRoles) {
    return (
      <div className="w-full flex justify-center items-center h-full">
        <NoDataFound />
      </div>
    );
  }

  const fieldsOrder = {
    firstName: 'order-1',
    lastName: 'order-2',
    username: 'order-3',
    email: 'order-4',
    phone: 'order-5',
    afm: 'order-6',
    password: 'order-7',
    password_confirmation: 'order-8',
    company: 'order-9',
    client_type: 'order-10',
    roles: 'order-11',
    invite_sent: 'order-12',
    active: 'order-12',
  };

  return (
    <Formik
      initialValues={userFormData?.data?.reduce((result, item) => {
        if (mode === 'edit') {
          result[item.name] = item.value;
        } else {
          if (item.name === 'roles') {
            result[item.name] = [];
          } else {
            if (item.type === 'checkbox') {
              result[item.name] = 0;
            } else {
              result[item.name] = item.value;
            }
          }
        }
        return result;
      }, {})}
      validationSchema={Yup.object({
        username: Yup.string().required(''),
        firstName: Yup.string().required(''),
        lastName: Yup.string().required(''),
        // name: Yup.string().required(''),
        password: Yup.string().when(mode, {
          is: () => mode === 'add',
          then: () => Yup.string()
            .required('Το πεδίο κωδικός είναι υποχρεωτικό.')
            .min(10, 'Ο κωδικός πρέπει να έχει τουλάχιστον 10 χαρακτήρες.')
            .test(
              'password-complexity',
              'Ο κωδικός δεν πληροί τις προυποθέσεις',
              (value) => {
                if (!value) return false; // Ensure password is not null

                // Define checks for each category
                const hasUppercase = /[A-Z]/.test(value);  // Uppercase letters
                const hasLowercase = /[a-z]/.test(value);  // Lowercase letters
                const hasDigit = /\d/.test(value);         // Digits
                const hasSpecialChar = /[~!@#$%^&*()_\-+=\\[\]{}:;"'<>,.?/]/.test(value);  // Special characters

                // Count how many categories are satisfied
                const validCategories = [hasUppercase, hasLowercase, hasDigit, hasSpecialChar].filter(Boolean).length;

                return validCategories >= 3;  // Password is valid if it meets at least 3 categories
              }
            ),
          otherwise: () => Yup.string()
            .notRequired() // If mode !== 'add', password is optional
            .min(10, 'Ο κωδικός πρέπει να έχει τουλάχιστον 10 χαρακτήρες.')
            .test(
              'password-complexity',
              'Ο κωδικός δεν πληροί τις προυποθέσεις',
              (value) => {
                if (!value) return true; // If password is empty, it's valid because it's not required
                
                // Same validation checks as above
                const hasUppercase = /[A-Z]/.test(value);
                const hasLowercase = /[a-z]/.test(value);
                const hasDigit = /\d/.test(value);
                const hasSpecialChar = /[~!@#$%^&*()_\-+=\\[\]{}:;"'<>,.?/]/.test(value);

                const validCategories = [hasUppercase, hasLowercase, hasDigit, hasSpecialChar].filter(Boolean).length;

                return validCategories >= 3;
              }
            )
        }),
        password_confirmation: Yup.string()
          .when('password', {
            is: (val) => val && val.length > 0, // Check only if password has a value
            then: () => Yup.string()
              .required('Το πεδίο επιβεβαίωσης κωδικού είναι υποχρεωτικό.')
              .oneOf([Yup.ref('password'), null], 'Οι κωδικοί δεν ταιριάζουν'),
            otherwise: () => Yup.string().notRequired()
          }),
        afm: Yup.string().required(''),
        client_type: Yup.string().when(mode, {
          is: () => mode === 'add',
          then: () => Yup.string().required(''),
          otherwise: () => Yup.string().notRequired(),
        }),
        email: Yup.string()
          .email('Το email πρέπει να είναι έγκυρο.')
          .required(''),
      })}
      onSubmit={async (values, { setErrors }) => {
        if (mode === 'edit') {
          if (!values.password) {
            delete values.password;
          }
          if (!values.client_type) {
            delete values.client_type;
          }

          updateUser(
            { userId, payload: values },
            {
              onSuccess: (data) => {
                if (data?.data === 'success') {
                  handleClose && handleClose();
                } else {
                  const { exceptions } = data;
                  if (exceptions && exceptions?.invalid) {
                    let errorObj = {};
                    Object.keys(exceptions.invalid).forEach((key) => {
                      errorObj[key] = exceptions.invalid[key][0];
                    });
                    setErrors(errorObj);
                  } else {
                    toast({
                      variant: 'destructive',
                      description: 'Παρουσιάστηκε κάποιο πρόβλημα',
                    });
                  }
                }
              },
            }
          );
        } else {
          addUser(values, {
            onSuccess: (data) => {
              if (data?.data === 'success') {
                handleClose && handleClose();
              } else {
                const { exceptions } = data;
                if (exceptions && exceptions?.invalid) {
                  let errorObj = {};
                  Object.keys(exceptions.invalid).forEach((key) => {
                    errorObj[key] = exceptions.invalid[key][0];
                  });
                  setErrors(errorObj);
                } else {
                  toast({
                    variant: 'destructive',
                    description: 'Παρουσιάστηκε κάποιο πρόβλημα',
                  });
                }
              }
            },
          });
        }
      }}
    >
      {({
        errors,
        touched,
        values,
        setFieldValue,
        isValid,
        dirty,
        handleBlur,
        ...props
      }) => {
        return (
          <Form
            tabIndex="0"
            className={`w-full ${
              mode === 'edit'
                ? 'max-lg:h-[calc(100vh-80px)]'
                : 'max-lg:h-[calc(100vh-130px)]'
            }  lg:max-h-[calc(100vh-8rem)] overflow-y-auto scrollbar pb-2 lg:pb-6 `}
          >
            <div className="grid lg:grid-cols-2 gap-5 driver-form lg:min-w-[500px] lg:max-w-[700px]">
              {userFormData?.data?.map((formElement) => {
                const { type, label, name } = formElement;
                if (type === 'checkbox') {
                  return (
                    <div
                      className={`items-top flex items-center space-x-2 ${fieldsOrder[name]}`}
                      key={name}
                    >
                      <Checkbox
                        id={name}
                        checked={values && !!values[name]}
                        onCheckedChange={(value) => {
                          setFieldValue(name, !!value ? 1 : 0);
                        }}
                      />
                      <div className="grid gap-1.5 leading-none">
                        <label
                          htmlFor={name}
                          className="text-label text-fadedgrey"
                        >
                          {label}
                        </label>
                      </div>
                    </div>
                  );
                }

                if (type === 'select_multiple') {
                  return (
                    <div
                      className={`lg:col-span-2 ${fieldsOrder[name]}`}
                      key={name}
                    >
                      <label
                        htmlFor={name}
                        className="text-label text-fadedgrey"
                      >
                        {label}
                      </label>
                      <div className="grid lg:grid-cols-3 grid-cols-2 gap-3 ">
                        {userRoles?.data.map((item) => {
                          return (
                            <div
                              className="items-top flex space-x-2 border border-fadedgrey border-solid p-4 rounded-lg"
                              key={item.id}
                            >
                              <label
                                htmlFor={item.id}
                                className="text-label text-fadedgrey "
                              >
                                {item.name}
                              </label>
                              <Checkbox
                                id={item.id}
                                checked={
                                  // Το Α.Φ.Μ. δεν είναι έγκυρο. The password confirmation does not match.
                                  values &&
                                  values['roles'] &&
                                  values['roles'].includes(item.name)
                                }
                                onCheckedChange={() => {
                                  if (values && values[name]) {
                                    if (values[name].includes(item.name)) {
                                      const newRoles = values[name].filter(
                                        (itemVal) => itemVal !== item.name
                                      );
                                      setFieldValue([name], newRoles);
                                    } else {
                                      setFieldValue(
                                        [name],
                                        [...values[name], item.name]
                                      );
                                    }
                                  } else {
                                    setFieldValue([name], [item.name]);
                                  }
                                }}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                }

                if (type === 'dropdown') {
                  // Παρακαλώ επιλέξτε
                  return (
                    <div className="order-8" key={name}>
                      <label
                        htmlFor={name}
                        className="text-label text-fadedgrey"
                      >
                        {label}
                      </label>
                      <Select
                        onValueChange={(value) => {
                          setFieldValue(name, value);
                        }}
                        value={values[name] === null ? undefined : values[name]}
                      >
                        <SelectTrigger
                          className={
                            touched[name] && errors.hasOwnProperty(name)
                              ? 'border border-solid border-primary'
                              : ''
                          }
                        >
                          <SelectValue placeholder="Παρακαλώ επιλέξτε" />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.keys(userTypes?.data).map((key) => {
                            return (
                              <SelectItem
                                key={key}
                                value={userTypes?.data[key]}
                              >
                                {key}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                  );
                }
                if(type === 'text' && name === 'phone') {
                    return (
                      <div className={`flex flex-col gap-1 ${fieldsOrder[name]}`} key={name}>
                        <label
                          htmlFor={name}
                          className="text-label text-fadedgrey"
                        >
                          {label}
                        </label>
                        <ReactPhoneInput
                          key={name}
                          id={name}
                          country="gr"
                          countryCodeEditable={true}
                          autoFormat={false}
                          containerClass={`flex gap-2 rounded-[8px] p-2 ${
                            touched[name] && errors.hasOwnProperty(name)
                              ? 'border border-primary'
                              : 'border border-[#A5A5A5]'
                          }`}
                          inputClass="placeholder:text-label"
                          localization={el}
                          specialLabel="Phone"
                          value={values[name]}
                          placeholder=""
                          onChange={(value) => {
                            const addedValue = mode !=='create' ? '+' +value : value 
                            setFieldValue(name, addedValue);
                          }}
                          onBlur={handleBlur}
                          error={!!touched[name] && errors[name]}
                          inputProps={{
                            name: formElement.name,
                            required: true,
                          }}
                        />
                      </div>
                    )
                }

                return (
                  <TextField
                    key={name}
                    id={name}
                    type={type}
                    autocomplete="off"
                    placeholder={label}
                    label={label}
                    onChange={(e) => {
                      setFieldValue(name, e.target.value);
                    }}
                    value={values[name]}
                    onBlur={handleBlur}
                    error={!!touched[name] && errors[name]}
                    wrapperClass={fieldsOrder[name]}
                    className={
                      touched[name] && errors.hasOwnProperty(name)
                        ? 'border border-solid border-primary'
                        : ''
                    }
                  />
                );
              })}
            </div>
            <div className="col-span-2 flex items-center justify-center gap-x-3.5 pt-6">
              <ModalFooter
                handleCancelClick={handleClose}
                type="submit"
                className="max-lg:flex-col max-lg:col-reverse max-lg:w-full"
                isLoading={isAddLoading || isEditLoading}
                saveBtnText="Υποβολή"
              />
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};
